'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: number;
  title: string;
  description: string;
  fileSize: string;
  icon: string;
}

interface DownloadResourcesProps {
  resources: Resource[];
}

export default function DownloadResources({ resources }: DownloadResourcesProps) {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = (resourceId: number) => {
    setDownloadingId(resourceId);
    setTimeout(() => {
      setDownloadingId(null);
    }, 2000);
  };

  return (
    <section className="bg-card py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-primary mb-4">
            Company Resources
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto">
            Download comprehensive company profiles, capability statements, and technical documentation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {resources.map((resource) => (
            <div
              key={resource.id}
              className="bg-surface rounded-lg p-6 shadow-subtle hover:shadow-modal transition-shadow duration-300"
            >
              <div className="flex items-start">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Icon
                    name={resource.icon as any}
                    size={24}
                    className="text-primary-foreground"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-lg font-headline font-bold text-textPrimary mb-2">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-textSecondary mb-3">
                    {resource.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-textSecondary">
                      {resource.fileSize}
                    </span>
                    <button
                      onClick={() => handleDownload(resource.id)}
                      disabled={downloadingId === resource.id}
                      className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-body font-semibold hover:bg-secondary transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingId === resource.id ? (
                        <>
                          <Icon name="ArrowPathIcon" size={16} className="mr-2 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Icon name="ArrowDownTrayIcon" size={16} className="mr-2" />
                          Download
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}